import React, { useEffect, useState } from 'react';
import ActionsPanel from './components/ActionsPanel';
import Header from './components/Header';
import NothingFoundCard from '../../../../components/generics/Cards/NothingFoundCard';
import { Table, TableCell, TableHead, TableRow } from '../../../../components/common/ResponsiveTable';
import { useTrans } from '../../../../services/i18n';
import { Grid, Button, TableContainer, List, ListItem, ListItemText } from '@material-ui/core';
import styles from './styles.module.css';
import connect from '../connect';
import { emptyArray } from '../../../../utils/arrayUtils';
import { ReactComponent as VendorsIcon } from '../icons/ico-vendor-management-empty.svg';
import { ReactComponent as NoArchivedIcon } from '../icons/ico-no-archived.svg';
import AddVendorManuallyForm from '../components/AddVendorManuallyForm';
import AddVendorForm from '../components/AddVendorForm';
import AddVendorTagsForm from '../components/AddVendorTagsForm';
import AddVendorsCsvForm from '../components/AddVendorsCsvForm';
import { CheckBox } from '../../../../components/common/Inputs/CheckBox';
import { formatDatePretty } from '../../../../utils/dateUtils';
import Loader from '../../../..//components/common/Loader';
import { makeStyles } from '@material-ui/core/styles';
import BorderColorOutlinedIcon from '@material-ui/icons/BorderColorOutlined';
import { ModalDialog } from '../../../../components/common/Dialogs';
import useBooleanHandlers from '../../../../hooks/useBooleanHandlers';
import { SpacingContainer } from '../../../../components/common/Containers/Base';
import SectionScrollable from '../../../../components/common/Containers/SectionScrollable';
import Typography from '../../../../components/common/Typography';
import { Divider } from '../../../../components/common/Divider';
import CodeDescription from './components/CodeDescription';
import TagsCell from './components/TagsCell';
import CodesCell from './components/CodesCell';
import { ROUTE_VENDORS_MANAGEMENT_DETAILS, ROUTE_VENDORS_DETAILS } from '../../../../constants/routes';
import { RouterLink } from '../../../../components/common/Buttons';
import { CSVLink } from 'react-csv';
import dayjs from 'dayjs';
import {
  getSbaCertificationDisplayName,
  getSbaCertificationExpirationDate,
  isSbaCertificationExpiringSoon,
} from './helpers';
import CrmOrganizationIndicator from './components/CrmOrganizationIndicator';

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
  linkStyle: {
    color: '#2445A7',
    cursor: 'pointer',
  },
  nameWithCrm: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    maxWidth: '100%',
  },
});

const modalNames = {
  naics: 'NAICS Codes',
  setAsides: 'Self Certified Set-Aside Codes',
  psc: 'PSC Codes',
  tags: 'Tags',
  sbaDetails: 'SBA Certification Codes',
};

const getVendorRedirectPath = (item) => {
  if (!item) return '';
  if (item.status === 'Unregistered') {
    return ROUTE_VENDORS_MANAGEMENT_DETAILS.path.replace(':id', item.id);
  }
  if (item.status === 'Registered') {
    return ROUTE_VENDORS_DETAILS.path.replace(':id', item.documentId);
  }
  return '';
};

const getVendorStatusLabel = (item) => {
  const isExpired = item.expirationDate && dayjs(item.expirationDate).isBefore(dayjs(), 'day');
  if (item.archived) return 'Archived';
  if (isExpired) return 'Expired';
  return item.status;
};

const VendorsManagementList = ({
  currentView,
  items,
  count,
  getList,
  itemCheck,
  filters,
  openAddVendorModal,
  openAddVendor,
  openAddVendorManuallyModal = false,
  openAddVendorManually,
  closeDrawer,
  openAddTagsModal,
  openAddTags,
  openImportVendorsCsvModal,
  openImportVendors,
  tableColumns,
  getVendorTags,
  pending,
  filterDefault,
  textSearch,
  changeFilters,
}) => {
  const { trans } = useTrans();
  const classes = useStyles();
  const [open, onOpen, onClose] = useBooleanHandlers();

  const [modalName, setModalName] = useState([]);
  const [codes, setCodes] = useState([]);
  const activeSearchText = (filters?.text || '').trim();
  const isSearchActive = !!activeSearchText;

  const showCodes = (column, codesList) => {
    setModalName(column);
    setCodes(codesList);
    onOpen();
  };

  useEffect(() => {
    getList({
      ...filters,
      type: filterDefault,
    });
  }, [filters, filterDefault, getList]);

  const getStatusStyleCell = (status, archived) => {
    if (archived) return styles.archived;
    if (status === 'Registered') return styles.registered;
    if (status === 'Unregistered' || status === 'Expired') return styles.unregistered;
    return undefined;
  };

  const onItemCheck = (ev, item) => {
    itemCheck({ id: item.id, checked: ev.target.checked });
  };

  const getTableCell = (c, item) => {
    if (c.value === 'name') {
      return (
        <TableCell className={classes.linkStyle}>
          <div className={classes.nameWithCrm}>
            <Typography color="primary" component={RouterLink} to={getVendorRedirectPath(item)} noWrap={false}>
              {item[c.value]}
            </Typography>
            <CrmOrganizationIndicator crmCompanyId={item.crmCompanyId} />
          </div>
        </TableCell>
      );
    }

    if (c.value === 'status') {
      const status = getVendorStatusLabel(item);
      return (
        <TableCell className={getStatusStyleCell(status, item.archived)}>
          <p>{status}</p>
        </TableCell>
      );
    }

    if (c.value === 'expirationDate') {
      const expiration = item[c.value];
      const isExpiringSoon = dayjs(expiration).diff(dayjs(), 'day') < 90;
      return (
        <TableCell>
          <Typography style={{ color: isExpiringSoon ? 'red' : undefined }}>{formatDatePretty(expiration)}</Typography>
        </TableCell>
      );
    }

    if (c.value === 'bussinessStartDate') {
      return <TableCell>{formatDatePretty(item[c.value])}</TableCell>;
    }

    if (c.value === 'tags') {
      return (
        <TableCell>
          <TagsCell tags={item.tags} onOpen={(tagsList) => showCodes('tags', tagsList)} />
        </TableCell>
      );
    }

    if (['naics', 'setAsides', 'psc', 'sbaDetails'].includes(c.value)) {
      return (
        <TableCell>
          <CodesCell columnValue={c.value} values={item[c.value]} onOpen={showCodes} />
        </TableCell>
      );
    }

    return <TableCell>{item[c.value]}</TableCell>;
  };

  useEffect(() => {
    getVendorTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getList]);

  const clearSearch = () => {
    const updated = {
      ...filters,
      text: '',
      pagination: { ...filters.pagination, page: 1 },
    };
    changeFilters(updated);
  };

  const exportHeaders = [];
  const exportData = [];
  if (items && items.length > 0) {
    exportHeaders.push(
      { label: 'Vendor Name', key: 'vendor-name' },
      { label: 'Status', key: 'status' },
      { label: 'Expiration Date', key: 'expiration-date' },
      { label: 'Cage Code', key: 'cage-code' },
      { label: 'DUNS Number', key: 'duns-number' },
      { label: 'UEI Code', key: 'uei-code' },
      { label: 'Business Start Date', key: 'business-start-date' },
      { label: 'Self Certifications', key: 'SetAsides' },
      { label: 'SBA Certifications', key: 'SbaDetails' },
      { label: 'NAICS', key: 'Naics' },
      { label: 'PSC', key: 'Psc' },
      { label: 'Tags', key: 'Tags' }
    );
    items.forEach((item) => {
      exportData.push({
        'vendor-name': item.name,
        status: item.status,
        'expiration-date': item.expirationDate,
        'cage-code': item.cageCode,
        'duns-number': item.dunsCode,
        'uei-code': item.ueiCode,
        'business-start-date': item.bussinessStartDate,
        SetAsides: item.setAsides ? item.setAsides.map((setAside) => setAside.code).join(', ') : '',
        SbaDetails: item.sbaCertifications ? item.sbaCertifications.map((sba) => sba.name).join(', ') : '',
        Naics: item.naics ? item.naics.map((naic) => naic.code).join(', ') : '',
        Psc: item.psc ? item.psc.map((psc) => psc.code).join(', ') : '',
        Tags: item.tags ? item.tags.join(', ') : '',
      });
    });
  }

  return (
    <>
      <div style={{ padding: '10px' }}>
        <Loader open={pending} />
        <AddVendorManuallyForm open={openAddVendorManuallyModal} onClose={() => openAddVendorManually(false)} />
        <AddVendorForm open={openAddVendorModal} onClose={() => openAddVendor(false)} />
        <AddVendorTagsForm open={openAddTagsModal} onClose={() => openAddTags(false)} />
        <AddVendorsCsvForm open={openImportVendorsCsvModal} onClose={() => openImportVendors(false)} />
        <Header closeDrawer={closeDrawer} />

        <Grid container spacing={2} justifyContent="flex-start" style={{ margin: '16px 0 0 0' }}>
          <Grid item>
            <Button variant="contained" color="primary" onClick={() => openAddVendor(true)}>
              {trans('add-vendor')}
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" onClick={() => openAddVendorManually(true)}>
              {trans('add-vendor-manually2')}
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" onClick={() => openImportVendors(true)}>
              {trans('import-csv')}
            </Button>
          </Grid>
          <Grid item>
            <CSVLink
              data={exportData}
              headers={exportHeaders}
              filename={'vendors_data.csv'}
              style={{ textDecoration: 'none' }}
            >
              <Button variant="contained" color="primary">
                {trans('export-vendors')}
              </Button>
            </CSVLink>
          </Grid>
        </Grid>
        <ActionsPanel />
        {isSearchActive && (
          <Grid container alignItems="center" style={{ margin: '12px 0' }} spacing={1}>
            <Grid item>
              <Typography variant="body2" color="hint">
                Showing results for: "{activeSearchText}"
              </Typography>
            </Grid>
            <Grid item>
              <Button size="small" color="primary" onClick={clearSearch}>
                Reset search
              </Button>
            </Grid>
          </Grid>
        )}
        {!emptyArray(items) ? (
          <TableContainer style={{ width: 'auto' }}>
            <Table
              className={classes.table}
              size="small"
              stickyHeader
              fixedLayout
              head={
                <TableHead>
                  <TableCell className={styles.tableHeadBg} width={60} noWrap></TableCell>
                  {tableColumns
                    .filter((i) => i.active)
                    .map((c) => (
                      <TableCell width={200} className={styles.tableHeadBg} key={c.id} noWrap>
                        {trans(c.name)}
                      </TableCell>
                    ))}
                </TableHead>
              }
            >
              {items.map(
                (item, index) =>
                  item && (
                    <TableRow key={index}>
                      <TableCell>
                        <CheckBox
                          color="secondary"
                          onChange={(ev) => onItemCheck(ev, item)}
                          checked={item.checked}
                          md={{ '& .MuiSvgIcon-root': { fontSize: 50 } }}
                        />
                      </TableCell>
                      {tableColumns.filter((i) => i.active).map((c) => getTableCell(c, item))}
                    </TableRow>
                  )
              )}
            </Table>
          </TableContainer>
        ) : currentView === 'vendors' && !pending ? (
          <NothingFoundCard
            title={isSearchActive ? `No vendors found for "${activeSearchText}"` : trans('you-add-no-vendors-yet')}
            description={
              isSearchActive ? 'Try a different search or reset to view all vendors.' : trans('all-manually-added')
            }
            size="large"
            Icon={<VendorsIcon />}
          >
            <br />
            <br />
            {isSearchActive ? (
              <Button color="primary" onClick={clearSearch}>
                Reset search
              </Button>
            ) : (
              <Button color="primary" onClick={() => openAddVendor(true)}>
                {trans('search-vendor')}
              </Button>
            )}
          </NothingFoundCard>
        ) : currentView === 'vendorsArchived' && !pending ? (
          <NothingFoundCard
            title={trans('your-archive-is-empty')}
            description={trans('all-archived-vendors')}
            size="large"
            Icon={<NoArchivedIcon />}
          ></NothingFoundCard>
        ) : (
          !pending && (
            <NothingFoundCard
              title={trans('your-draft-is-empty')}
              description={trans('all-draft-vendors')}
              size="large"
              Icon={<BorderColorOutlinedIcon style={{ width: '64px', height: '64px' }} />}
            ></NothingFoundCard>
          )
        )}
      </div>
      <ModalDialog open={open} onClose={onClose} disableContentSpacing title={modalNames[modalName]}>
        <SectionScrollable dividedFixed>
          <SpacingContainer px={4} py={1}>
            <List>
              {codes.map((item, index) => {
                const sbaExpirationDate = modalName === 'sbaDetails' ? getSbaCertificationExpirationDate(item) : null;
                const showSbaExpirationDate = modalName === 'sbaDetails' && isSbaCertificationExpiringSoon(item);

                return (
                  <React.Fragment key={item.code || item.name || item}>
                    <ListItem disableGutters>
                      <ListItemText>
                        <Typography fontFamily="lexend" color="primary" variant="body1" weight="bold">
                          {modalName === 'sbaDetails' ? getSbaCertificationDisplayName(item) : item.code || item}
                          {showSbaExpirationDate && (
                            <span style={{ color: 'red', marginLeft: 8 }}>
                              Expires {formatDatePretty(sbaExpirationDate)}
                            </span>
                          )}
                        </Typography>
                        {item.description && (
                          <Grid container>
                            <Grid item>
                              <CodeDescription item={item} />
                            </Grid>
                          </Grid>
                        )}
                      </ListItemText>
                    </ListItem>

                    {index + 1 < codes.length && <Divider light spacing={0} />}
                  </React.Fragment>
                );
              })}
            </List>
          </SpacingContainer>
        </SectionScrollable>
      </ModalDialog>
    </>
  );
};

export default connect(VendorsManagementList);
